import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Archive,
  Download,
  FileSpreadsheet,
  FileText,
  Layers,
  Loader2,
  Play,
  RefreshCw,
  Trash2,
  Users,
  X,
  Zap,
} from "lucide-react";
import API from "../../services/api";
import { AuthContext } from "../Context/AuthContext";
import { useToast } from "../Context/ToastContext";
import { useEmployees } from "../../hooks/useEmployees";
import PayrollRunSelect from "../../components/common/PayrollRunSelect";
import EmployeeMultiSelect from "../../components/common/EmployeeMultiSelect";
import {
  cancelBulkPayslipJob,
  createBulkPayslipJob,
  deleteBulkPayslipJob,
  downloadBulkPayslipFile,
  getBulkPayslipJob,
  listBulkPayslipJobs,
  previewBulkPayslips,
  retryBulkPayslipJob,
  saveBulkDownload,
} from "./bulkPayslipApi";
import { toDisplayString } from "../Bank/bankErrorUtils";
import { getApiErrorDetail } from "../../utils/idempotency";
import "./BulkPayslips.css";

function apiErrorMessage(err, fallback = "Request failed") {
  return toDisplayString(getApiErrorDetail(err)) || fallback;
}

const FORMAT_OPTIONS = [
  { key: "individual_pdf", label: "Individual PDFs", icon: FileText },
  { key: "zip", label: "ZIP (all PDFs)", icon: Archive },
  { key: "merged_pdf", label: "Merged PDF", icon: Layers },
  { key: "xlsx", label: "Excel Summary", icon: FileSpreadsheet },
  { key: "csv", label: "CSV Summary", icon: FileSpreadsheet },
];

const STATUS_CLASS = {
  queued: "bps-status--queued",
  running: "bps-status--running",
  completed: "bps-status--completed",
  completed_with_errors: "bps-status--completed_with_errors",
  failed: "bps-status--failed",
  cancelled: "bps-status--cancelled",
};

const FILE_KEYS = [
  { key: "zip", label: "ZIP" },
  { key: "merged_pdf", label: "PDF" },
  { key: "xlsx", label: "Excel" },
  { key: "csv", label: "CSV" },
  { key: "failed_report", label: "Failed" },
];

function formatBytes(kb) {
  if (!kb) return "—";
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatDuration(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

export default function BulkPayslips() {
  const toast = useToast();
  const { user } = useContext(AuthContext);
  const { employees } = useEmployees();

  const [runId, setRunId] = useState("");
  const [selectionMode, setSelectionMode] = useState("all");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [employeeType, setEmployeeType] = useState("");
  const [employeeCategory, setEmployeeCategory] = useState("");
  const [costCenter, setCostCenter] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("active");
  const [search, setSearch] = useState("");
  const [formats, setFormats] = useState(["zip", "xlsx"]);
  const [regenerate, setRegenerate] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    Promise.all([
      API.get("/setup/departments").then((r) => r.data || []).catch(() => []),
      API.get("/setup/designations").then((r) => r.data || []).catch(() => []),
      API.get("/setup/locations").then((r) => r.data || []).catch(() => []),
    ]).then(([depts, desigs, locs]) => {
      setDepartments(depts);
      setDesignations(desigs);
      setLocations(locs);
    });
    const types = [...new Set(employees.map((e) => e.employee_type).filter(Boolean))];
    setEmployeeTypes(types);
  }, [employees]);

  const filters = useMemo(
    () => ({
      selection_mode: selectionMode,
      employee_ids: selectionMode === "selected" ? selectedEmployeeIds : [],
      department_id: departmentId || null,
      designation_id: designationId || null,
      location_id: locationId || null,
      branch_id: locationId || null,
      employee_type: employeeType || null,
      employee_category: employeeCategory || null,
      cost_center: costCenter || null,
      employment_status: employmentStatus,
      search: search.trim() || null,
    }),
    [
      selectionMode,
      selectedEmployeeIds,
      departmentId,
      designationId,
      locationId,
      employeeType,
      employeeCategory,
      costCenter,
      employmentStatus,
      search,
    ]
  );

  const filterChips = useMemo(() => {
    const chips = [];
    if (selectionMode !== "all") chips.push({ label: `Mode: ${selectionMode}` });
    if (departmentId) {
      const d = departments.find((x) => String(x.department_id) === String(departmentId));
      if (d) chips.push({ label: `Dept: ${d.name}` });
    }
    if (locationId) {
      const l = locations.find((x) => String(x.location_id) === String(locationId));
      if (l) chips.push({ label: `Branch: ${l.name}` });
    }
    if (employeeType) chips.push({ label: `Type: ${employeeType}` });
    if (employmentStatus !== "active") chips.push({ label: `Status: ${employmentStatus}` });
    if (search.trim()) chips.push({ label: `Search: ${search.trim()}` });
    return chips;
  }, [selectionMode, departmentId, locationId, employeeType, employmentStatus, search, departments, locations]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await listBulkPayslipJobs({ limit: 30 });
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const runPreview = useCallback(async () => {
    if (!runId) {
      toast.error("Select a payroll month first.");
      return;
    }
    setPreviewLoading(true);
    setError("");
    try {
      const data = await previewBulkPayslips({ payroll_run_id: runId, filters });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setError(apiErrorMessage(e, "Preview failed"));
    } finally {
      setPreviewLoading(false);
    }
  }, [runId, filters, toast]);

  useEffect(() => {
    if (!runId) {
      setPreview(null);
      return;
    }
    const t = setTimeout(runPreview, 400);
    return () => clearTimeout(t);
  }, [runId, filters, runPreview]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    (jobId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const job = await getBulkPayslipJob(jobId);
          setActiveJob(job);
          if (!["queued", "running"].includes(job.status)) {
            stopPolling();
            setGenerating(false);
            loadHistory();
          }
        } catch {
          stopPolling();
          setGenerating(false);
        }
      }, 2000);
    },
    [stopPolling, loadHistory]
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startGeneration = async () => {
    if (!runId) {
      toast.error("Payroll month is required.");
      return;
    }
    if (!formats.length) {
      toast.error("Select at least one output format.");
      return;
    }
    setGenerating(true);
    setError("");
    setShowProgress(true);
    try {
      const job = await createBulkPayslipJob({
        payroll_run_id: runId,
        output_formats: formats,
        filters,
        regenerate,
        live: true,
      });
      setActiveJob(job);
      pollJob(job.job_id);
      toast.success("Bulk generation started in the background.");
    } catch (e) {
      setGenerating(false);
      setShowProgress(false);
      setError(apiErrorMessage(e, "Failed to start job"));
      toast.error(apiErrorMessage(e, "Failed to start job"));
    }
  };

  const handleCancel = async () => {
    if (!activeJob?.job_id) return;
    try {
      const job = await cancelBulkPayslipJob(activeJob.job_id, "Cancelled by user");
      setActiveJob(job);
      stopPolling();
      setGenerating(false);
      toast.info("Job cancelled.");
      loadHistory();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Cancel failed"));
    }
  };

  const handleDownload = async (jobId, fileKey, label) => {
    try {
      const res = await downloadBulkPayslipFile(jobId, fileKey);
      await saveBulkDownload(res, `${label}.bin`);
    } catch {
      toast.error(`${label} file is not available.`);
    }
  };

  const lastJob = history[0];
  const isJobRunning = activeJob && ["queued", "running"].includes(activeJob.status);

  return (
    <div className="bps-root">
      {error && <div className="bps-alert bps-alert--error">{toDisplayString(error)}</div>}

      <div className="bps-dashboard">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bps-card">
          <div className="bps-card-label">Employees selected</div>
          <div className="bps-card-value">
            {previewLoading ? "…" : preview?.employee_count ?? "—"}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bps-card">
          <div className="bps-card-label">Est. output size</div>
          <div className="bps-card-value">{formatBytes(preview?.estimated_size_kb)}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bps-card">
          <div className="bps-card-label">Formats</div>
          <div className="bps-card-value" style={{ fontSize: "0.9rem" }}>
            {formats.length ? formats.join(", ") : "—"}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bps-card">
          <div className="bps-card-label">Status</div>
          <div className="bps-card-value" style={{ fontSize: "0.85rem" }}>
            {isJobRunning ? (
              <span className={`bps-status ${STATUS_CLASS.running}`}>Running</span>
            ) : lastJob ? (
              <span className={`bps-status ${STATUS_CLASS[lastJob.status] || ""}`}>{lastJob.status}</span>
            ) : (
              "Ready"
            )}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bps-card">
          <div className="bps-card-label">Last job</div>
          <div className="bps-card-value" style={{ fontSize: "0.8rem" }}>
            {lastJob?.payroll_month_label || "—"}
          </div>
        </motion.div>
      </div>

      <div className="bps-panel">
        <div className="bps-panel-head">
          <span className="bps-panel-title">Payroll filters</span>
          <button type="button" className="bps-btn bps-btn--secondary" onClick={runPreview} disabled={!runId || previewLoading}>
            <RefreshCw size={14} className={previewLoading ? "animate-spin" : ""} />
            Refresh count
          </button>
        </div>
        <div className="bps-panel-body">
          <div className="bps-filters">
            <div className="bps-field" style={{ gridColumn: "1 / -1" }}>
              <PayrollRunSelect
                value={runId}
                onChange={setRunId}
                label="Payroll month *"
                required
                emptyLabel="Select processed payroll month"
              />
            </div>
            <div className="bps-field">
              <label>Selection mode</label>
              <select value={selectionMode} onChange={(e) => setSelectionMode(e.target.value)}>
                <option value="all">All employees</option>
                <option value="selected">Selected employees</option>
                <option value="department">By department</option>
                <option value="branch">By branch / location</option>
                <option value="employee_type">By employee type</option>
                <option value="designation">By designation</option>
              </select>
            </div>
            <div className="bps-field">
              <label>Employment status</label>
              <select value={employmentStatus} onChange={(e) => setEmploymentStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="resigned">Resigned</option>
                <option value="all">All</option>
              </select>
            </div>
            <div className="bps-field">
              <label>Department</label>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                <option value="">All departments</option>
                {departments.map((d) => (
                  <option key={d.department_id} value={d.department_id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="bps-field">
              <label>Designation</label>
              <select value={designationId} onChange={(e) => setDesignationId(e.target.value)}>
                <option value="">All designations</option>
                {designations.map((d) => (
                  <option key={d.designation_id} value={d.designation_id}>{d.title || d.name}</option>
                ))}
              </select>
            </div>
            <div className="bps-field">
              <label>Branch / location</label>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                <option value="">All locations</option>
                {locations.map((l) => (
                  <option key={l.location_id} value={l.location_id}>{l.name}</option>
                ))}
              </select>
            </div>
            <div className="bps-field">
              <label>Employee type</label>
              <select value={employeeType} onChange={(e) => setEmployeeType(e.target.value)}>
                <option value="">All types</option>
                {employeeTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="bps-field">
              <label>Employee category</label>
              <input value={employeeCategory} onChange={(e) => setEmployeeCategory(e.target.value)} placeholder="From employee metadata" />
            </div>
            <div className="bps-field">
              <label>Cost center</label>
              <input value={costCenter} onChange={(e) => setCostCenter(e.target.value)} placeholder="Optional" />
            </div>
            <div className="bps-field">
              <label>Employee search</label>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, code, email…" />
            </div>
          </div>

          {selectionMode === "selected" && (
            <div className="mt-4">
              <EmployeeMultiSelect
                value={selectedEmployeeIds}
                onChange={setSelectedEmployeeIds}
                label="Selected employees"
                hint="Add employees to include in this batch"
              />
            </div>
          )}

          {filterChips.length > 0 && (
            <div className="bps-chips">
              {filterChips.map((c) => (
                <span key={c.label} className="bps-chip">{c.label}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bps-panel">
        <div className="bps-panel-head">
          <span className="bps-panel-title">Output formats</span>
        </div>
        <div className="bps-panel-body">
          <div className="bps-formats">
            {FORMAT_OPTIONS.map(({ key, label, icon: Icon }) => {
              const on = formats.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`bps-format-opt ${on ? "bps-format-opt--on" : ""}`}
                  onClick={() =>
                    setFormats((prev) => (on ? prev.filter((f) => f !== key) : [...prev, key]))
                  }
                >
                  <Icon size={15} />
                  {label}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} />
            Regenerate (allow duplicate filter combination)
          </label>
        </div>
      </div>

      <div className="bps-actions">
        <button
          type="button"
          className="bps-btn bps-btn--primary"
          disabled={generating || !runId || !preview?.eligible}
          onClick={startGeneration}
        >
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
          {generating ? "Generating…" : "Generate bulk payslips"}
        </button>
        {lastJob?.file_paths?.zip && (
          <button
            type="button"
            className="bps-btn bps-btn--secondary"
            onClick={() => handleDownload(lastJob.job_id, "zip", "payslips")}
          >
            <Zap size={16} />
            Quick download last ZIP
          </button>
        )}
      </div>

      {showProgress && activeJob && (
        <div className="bps-progress-overlay" role="dialog" aria-modal="true">
          <div className="bps-progress-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-slate-900">Bulk generation progress</h3>
              {!isJobRunning && (
                <button type="button" onClick={() => setShowProgress(false)} className="text-slate-400 hover:text-slate-700">
                  <X size={20} />
                </button>
              )}
            </div>
            <span className={`bps-status ${STATUS_CLASS[activeJob.status] || ""}`}>{activeJob.status}</span>
            <div className="bps-progress-bar">
              <div className="bps-progress-fill" style={{ width: `${activeJob.progress_percent || 0}%` }} />
            </div>
            <p className="text-2xl font-bold text-slate-900">{activeJob.progress_percent || 0}%</p>
            <div className="grid grid-cols-2 gap-3 text-sm text-slate-600 mt-3">
              <div>
                <strong className="text-slate-800">Processed</strong>
                <br />
                {(activeJob.successful_count || 0) + (activeJob.failed_count || 0)} / {activeJob.total_employees}
              </div>
              <div>
                <strong className="text-slate-800">Current</strong>
                <br />
                {activeJob.current_employee_name || "—"}
              </div>
              <div>
                <strong className="text-slate-800">Speed</strong>
                <br />
                {activeJob.processing_speed ? `${activeJob.processing_speed}/min` : "—"}
              </div>
              <div>
                <strong className="text-slate-800">ETA</strong>
                <br />
                {formatDuration(activeJob.estimated_seconds_remaining)}
              </div>
              <div>
                <strong className="text-slate-800">Files</strong>
                <br />
                {activeJob.generated_file_count || 0}
              </div>
              <div>
                <strong className="text-slate-800">Duration</strong>
                <br />
                {formatDuration(activeJob.duration_seconds)}
              </div>
            </div>

            {!isJobRunning && (
              <>
                <div className="bps-summary-grid">
                  <div className="bps-summary-item">
                    <strong>{activeJob.successful_count}</strong>
                    <span>Successful</span>
                  </div>
                  <div className="bps-summary-item">
                    <strong>{activeJob.failed_count}</strong>
                    <span>Failed</span>
                  </div>
                  <div className="bps-summary-item">
                    <strong>{activeJob.skipped_count}</strong>
                    <span>Skipped</span>
                  </div>
                </div>
                <div className="bps-downloads mt-4">
                  {FILE_KEYS.map(({ key, label }) =>
                    activeJob.file_paths?.[key] ? (
                      <button
                        key={key}
                        type="button"
                        className="bps-dl-btn"
                        onClick={() => handleDownload(activeJob.job_id, key, label)}
                      >
                        <Download size={12} style={{ display: "inline", marginRight: 4 }} />
                        {label}
                        {activeJob.file_sizes?.[key]
                          ? ` (${formatBytes(Math.round(activeJob.file_sizes[key] / 1024))})`
                          : ""}
                      </button>
                    ) : null
                  )}
                </div>
                <button type="button" className="bps-btn bps-btn--secondary mt-4 w-full" onClick={() => setShowProgress(false)}>
                  Close
                </button>
              </>
            )}

            {isJobRunning && (
              <button type="button" className="bps-btn bps-btn--danger mt-4 w-full" onClick={handleCancel}>
                Cancel job
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bps-panel">
        <div className="bps-panel-head">
          <span className="bps-panel-title">Generation history</span>
          <button type="button" className="bps-btn bps-btn--secondary" onClick={loadHistory}>
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
        <div className="bps-panel-body bps-table-wrap">
          {historyLoading ? (
            <div className="space-y-2">
              <div className="bps-skeleton" />
              <div className="bps-skeleton" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-500">No bulk generation jobs yet.</p>
          ) : (
            <table className="bps-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Payroll month</th>
                  <th>By</th>
                  <th>Employees</th>
                  <th>Formats</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Duration</th>
                  <th>Downloads</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((job) => (
                  <tr key={job.job_id}>
                    <td title={job.job_id}>{job.job_id.slice(0, 8)}…</td>
                    <td>{job.payroll_month_label || "—"}</td>
                    <td>{job.generated_by_name || "—"}</td>
                    <td>{job.employee_count}</td>
                    <td>{(job.output_formats || []).join(", ")}</td>
                    <td>
                      <span className={`bps-status ${STATUS_CLASS[job.status] || ""}`}>{job.status}</span>
                    </td>
                    <td>{job.created_at ? new Date(job.created_at).toLocaleString() : "—"}</td>
                    <td>{formatDuration(job.duration_seconds)}</td>
                    <td>
                      <div className="bps-downloads">
                        {FILE_KEYS.map(({ key, label }) =>
                          job.file_paths?.[key] ? (
                            <button
                              key={key}
                              type="button"
                              className="bps-dl-btn"
                              onClick={() => handleDownload(job.job_id, key, label)}
                            >
                              {label}
                            </button>
                          ) : null
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          className="bps-dl-btn"
                          title="View details"
                          onClick={() => {
                            setActiveJob(job);
                            setShowProgress(true);
                          }}
                        >
                          <Users size={12} />
                        </button>
                        {job.failed_count > 0 && (
                          <button
                            type="button"
                            className="bps-dl-btn"
                            title="Retry failed"
                            onClick={async () => {
                              try {
                                const updated = await retryBulkPayslipJob(job.job_id);
                                setActiveJob(updated);
                                setShowProgress(true);
                                pollJob(updated.job_id);
                                setGenerating(true);
                              } catch (e) {
                                toast.error(apiErrorMessage(e, "Retry failed"));
                              }
                            }}
                          >
                            <RefreshCw size={12} />
                          </button>
                        )}
                        {!["queued", "running"].includes(job.status) && (
                          <button
                            type="button"
                            className="bps-dl-btn"
                            title="Delete history"
                            onClick={async () => {
                              if (!window.confirm("Delete this job history and files?")) return;
                              try {
                                await deleteBulkPayslipJob(job.job_id);
                                toast.success("Deleted.");
                                loadHistory();
                              } catch (e) {
                                toast.error(apiErrorMessage(e, "Delete failed"));
                              }
                            }}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
